import { Helmet } from 'react-helmet-async';
import {
  defaultSEOConfig,
  generatePageTitle,
  generateCanonicalUrl,
  generateImageUrl,
  formatMetaDescription,
  type SEOConfig,
} from '@/utils/seo';

export interface SEOProps {
  /**
   * Page title (will be appended with site name)
   */
  title?: string;

  /**
   * Meta description for the page
   */
  description?: string;

  /**
   * Canonical URL path (e.g., '/about' or full URL)
   */
  canonical?: string;

  /**
   * Open Graph image URL or path
   */
  ogImage?: string;

  /**
   * Open Graph type (default: 'website')
   */
  ogType?: 'website' | 'article' | 'music.song' | 'music.album' | 'music.playlist' | 'profile';

  /**
   * Keywords for the page (comma-separated or array)
   */
  keywords?: string | string[];

  /**
   * JSON-LD structured data
   */
  structuredData?: object | object[];

  /**
   * Article-specific metadata (when ogType is 'article')
   */
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };

  /**
   * Override default SEO configuration
   */
  config?: Partial<SEOConfig>;

  /**
   * Twitter card type (default: 'summary_large_image')
   */
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';

  /**
   * Disable indexing for this page
   */
  noIndex?: boolean;

  /**
   * Additional meta tags
   */
  additionalMetaTags?: Array<{
    name?: string;
    property?: string;
    content: string;
  }>;

  /**
   * Additional link tags
   */
  additionalLinks?: Array<{
    rel: string;
    href: string;
    [key: string]: string;
  }>;
}

/**
 * SEO component for managing page meta tags, Open Graph, Twitter Cards, and structured data
 *
 * @example
 * ```tsx
 * <SEO
 *   title="About Us"
 *   description="Learn about Atlantic Music Festival"
 *   canonical="/about"
 *   ogImage="/images/about-og.jpg"
 * />
 * ```
 */
export function SEO({
  title,
  description,
  canonical,
  ogImage,
  ogType = 'website',
  keywords,
  structuredData,
  article,
  config: customConfig,
  twitterCard = 'summary_large_image',
  noIndex = false,
  additionalMetaTags = [],
  additionalLinks = [],
}: SEOProps) {
  const config = { ...defaultSEOConfig, ...customConfig };

  // Generate page title
  const pageTitle = generatePageTitle(title, config);

  // Format description
  const pageDescription = formatMetaDescription(
    description || config.defaultDescription
  );

  // Generate canonical URL
  const canonicalUrl = canonical
    ? canonical.startsWith('http')
      ? canonical
      : generateCanonicalUrl(canonical, config)
    : config.siteUrl;

  // Generate image URL
  const imageUrl = generateImageUrl(ogImage || config.defaultImage, config);

  // Format keywords
  const keywordsString = Array.isArray(keywords)
    ? keywords.join(', ')
    : keywords;

  // Convert structured data to JSON string
  const structuredDataArray = Array.isArray(structuredData)
    ? structuredData
    : structuredData
    ? [structuredData]
    : [];

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {keywordsString && <meta name="keywords" content={keywordsString} />}

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={config.siteName} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={title || config.siteName} />
      {config.locale && <meta property="og:locale" content={config.locale} />}

      {/* Article-specific Open Graph tags */}
      {ogType === 'article' && article && (
        <>
          {article.publishedTime && (
            <meta property="article:published_time" content={article.publishedTime} />
          )}
          {article.modifiedTime && (
            <meta property="article:modified_time" content={article.modifiedTime} />
          )}
          {article.author && (
            <meta property="article:author" content={article.author} />
          )}
          {article.section && (
            <meta property="article:section" content={article.section} />
          )}
          {article.tags?.map((tag) => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={title || config.siteName} />
      {config.twitterHandle && (
        <meta name="twitter:site" content={config.twitterHandle} />
      )}

      {/* Additional Meta Tags */}
      {additionalMetaTags.map((tag, index) => {
        if (tag.name) {
          return <meta key={`meta-${index}`} name={tag.name} content={tag.content} />;
        }
        if (tag.property) {
          return <meta key={`meta-${index}`} property={tag.property} content={tag.content} />;
        }
        return null;
      })}

      {/* Additional Links */}
      {additionalLinks.map((link, index) => (
        <link key={`link-${index}`} {...link} />
      ))}

      {/* Structured Data (JSON-LD) */}
      {structuredDataArray.map((data, index) => (
        <script
          key={`structured-data-${index}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
      ))}
    </Helmet>
  );
}

export default SEO;